// W4 — Visual auto-approval rule builder.
//
// Owners / team_leads / managers (capability `proposals.approve`) build KPI
// threshold rules that auto-approve proposals when ALL conditions are met
// (e.g. "auto-approve budget changes < 10% when ROAS > 3 and spend < 500").
// analyst / read_only see the rules read-only (no Add / Edit / toggle / Delete).
//
// Backend is the LIVE `/api/v1/agency/auto-approval-rules` family (agency
// Bearer). The page keeps the legacy Actions sub-page chrome (SubPage shell,
// MainSquareButton, Popup, custom SCSS, setMessage toasts) — NO MUI.
//
// Scope: rules are listed for the active shop (`shop={active_shop_slug}`) plus
// any org-wide defaults the backend returns. The create/edit popup lets the
// author target an org-wide default OR a specific accessible shop.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import ActionCenterTabs from '../ActionCenterTabs';
import { HOME_ROUTE } from '../../../lib/constants/route';
import {
  plusSignSVG,
  trash2SVG,
  warningWithCircleSVG,
  clockSVG,
} from '../../../lib/allCustomSVGs';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import Popup from '../../../ui/popup/Popup';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import {
  listAutoApprovalRules,
  createAutoApprovalRule,
  updateAutoApprovalRule,
  deleteAutoApprovalRule,
  listRecentMatches,
  canManageRule,
  ORG_WIDE_RULE_REQUIRES_ALL_BRANDS,
  AutoApprovalRule,
  RuleCondition,
  RuleMetric,
  RuleOperator,
  RuleRiskLevel,
  RecentMatch,
  CreateAutoApprovalRuleInput,
} from '../../../lib/api/auto-approval/auto-approval-api';
import { setMessage } from '../../../store/reducers/appSlice';
import { useEmployeeRole, can } from '../../../lib/rbac';
import type { RootState } from '../../../store/store';
import { PROPOSAL_PLATFORM_OPTIONS } from '../actionQueue/proposalFormat';
import NoAccessNotice from '../../../components/noAccess/NoAccessNotice';
import isAccessDeniedError from '../../../lib/helper-functions/isAccessDeniedError';
import '../actions.scss';

const RECENT_MATCHES_LIMIT = 8;

const METRIC_OPTIONS: RuleMetric[] = [
  'change_percentage',
  'budget_delta',
  'roas',
  'spend',
  'risk_level',
];

const OPERATOR_OPTIONS: RuleOperator[] = ['<', '<=', '>', '>=', '=='];

const RISK_LEVEL_OPTIONS: RuleRiskLevel[] = ['low', 'medium', 'high'];

// Risk-level ordering used to render the `risk_level` metric as an ordinal
// dropdown rather than a free number.
const RISK_VALUE_OPTIONS = ['low', 'medium', 'high', 'critical'];

const PLATFORM_OPTIONS = PROPOSAL_PLATFORM_OPTIONS;

const ORG_DEFAULT_SCOPE = '__org__';

interface ConditionDraft {
  metric: RuleMetric;
  operator: RuleOperator;
  value: string;
}

interface FormState {
  name: string;
  scope: string; // ORG_DEFAULT_SCOPE or a shop slug
  platform: string;
  tool_name: string;
  conditions: ConditionDraft[];
  max_risk_level: RuleRiskLevel;
  cap_enabled: boolean;
  max_daily_auto_approvals: string;
  is_active: boolean;
}

const emptyCondition = (): ConditionDraft => ({
  metric: 'change_percentage',
  operator: '<',
  value: '',
});

const initialFormState = (): FormState => ({
  name: '',
  scope: ORG_DEFAULT_SCOPE,
  platform: '',
  tool_name: '',
  conditions: [emptyCondition()],
  max_risk_level: 'low',
  cap_enabled: false,
  max_daily_auto_approvals: '10',
  is_active: true,
});

/** Whether a metric's value is an ordinal risk level (dropdown) vs a number. */
const isRiskMetric = (metric: RuleMetric) => metric === 'risk_level';

/** Compact "time ago" for the recent-matches feed; falls back to absolute. */
function timeAgo(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ActionRules = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // W1 — managing auto-approval rules is an approval-policy write. Gate to
  // owner / team_lead / manager via `proposals.approve`. analyst & read_only
  // see the rules read-only (no Add / Edit / toggle / Delete affordances).
  const role = useEmployeeRole();
  const canManageRules = can(role, 'proposals.approve');

  // Scope source (mirrors ActionQueue.tsx): the legacy "currently selected"
  // shop drives which shop's rules we list; accessible_shops feeds the scope
  // selector in the create/edit popup.
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const accessibleShops = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.accessible_shops || []
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );

  // Accessible shops as {slug,name} — used for the scope dropdown + label
  // resolution. Owners auto-bridged from a seller login have an empty
  // accessible_shops list but still own brands via all_shops.
  const shopOptions = useMemo(() => {
    const out: Array<{ slug: string; name: string }> = [];
    const seen = new Set<string>();
    const push = (slug?: string, name?: string) => {
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      out.push({ slug, name: name || slug });
    };
    accessibleShops.forEach((s) => push(s.slug ?? s.brand_slug, s.name ?? s.brand_name));
    (allShops as Array<{ slug?: string; name?: string }>).forEach((s) =>
      push(s.slug, s.name)
    );
    if (activeShopSlug) push(activeShopSlug, activeShopSlug);
    return out;
  }, [accessibleShops, allShops, activeShopSlug]);

  const shopNameForSlug = useCallback(
    (slug: string | null | undefined): string => {
      if (!slug) return slug || '';
      return shopOptions.find((s) => s.slug === slug)?.name || slug;
    },
    [shopOptions]
  );

  const [rules, setRules] = useState<AutoApprovalRule[]>([]);
  const [recentMatches, setRecentMatches] = useState<RecentMatch[]>([]);
  const [loading, setLoading] = useState(true);
  // Visible error state — set when the rules fetch fails so the page renders a
  // clear, retryable error inside the shell instead of a misleading "no rules"
  // empty state (or, worse, a blank page).
  const [loadError, setLoadError] = useState<string | null>(null);
  // The rules endpoint refused this member (401/403): show the no-access
  // message instead of a raw "forbidden" error with a Retry.
  const [accessDenied, setAccessDenied] = useState(false);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoApprovalRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<AutoApprovalRule | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState());

  const loadRules = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setAccessDenied(false);
    try {
      const shop = activeShopSlug || undefined;
      // Recent matches are best-effort (their failure must never sink the whole
      // page), but the rules list is load-bearing — let it surface its error.
      const [ruleList, matches] = await Promise.all([
        listAutoApprovalRules(shop),
        listRecentMatches(shop, RECENT_MATCHES_LIMIT).catch(() => []),
      ]);
      setRules(Array.isArray(ruleList) ? ruleList : []);
      setRecentMatches(Array.isArray(matches) ? matches : []);
    } catch (error: any) {
      console.error('Failed to load auto-approval rules:', error);
      setRules([]);
      setRecentMatches([]);
      if (isAccessDeniedError(error)) {
        setAccessDenied(true);
        return;
      }
      setLoadError(error?.message || t('FailedToLoadRules'));
    } finally {
      setLoading(false);
    }
  }, [activeShopSlug, t]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  /**
   * The text for a failed create, edit or delete: our own wording for the
   * "needs access to every brand" refusal, otherwise the server message.
   */
  const ruleErrorMessage = (error: any, fallback: string): string =>
    error?.code === ORG_WIDE_RULE_REQUIRES_ALL_BRANDS
      ? t('RuleOrgWideRequiresAllBrands')
      : error?.message || fallback;

  // ---------- metric / operator / risk labels ----------

  const metricLabel = (metric: RuleMetric): string => {
    switch (metric) {
      case 'change_percentage':
        return t('MetricChangePercentage');
      case 'budget_delta':
        return t('MetricBudgetDelta');
      case 'roas':
        return t('MetricRoas');
      case 'spend':
        return t('MetricSpend');
      case 'risk_level':
        return t('MetricRiskLevel');
      default:
        return metric;
    }
  };

  const operatorLabel = (op: RuleOperator): string => {
    switch (op) {
      case '<':
        return t('OperatorLessThan');
      case '<=':
        return t('OperatorLessThanOrEqual');
      case '>':
        return t('OperatorGreaterThan');
      case '>=':
        return t('OperatorGreaterThanOrEqual');
      case '==':
        return t('OperatorEquals');
      default:
        return op;
    }
  };

  const riskLabel = (risk: string | null | undefined): string => {
    switch (risk) {
      case 'low':
        return t('RiskLow');
      case 'medium':
        return t('RiskMedium');
      case 'high':
        return t('RiskHigh');
      case 'critical':
        return t('RiskCritical');
      default:
        return risk || '—';
    }
  };

  const platformLabel = (platform: string | null): string => {
    if (!platform) return t('AllPlatforms');
    return (
      PLATFORM_OPTIONS.find((p) => p.value === platform)?.label || platform
    );
  };

  /** Render a single condition as readable text, e.g. "ROAS > 3". */
  const conditionText = (c: { metric: RuleMetric; operator: RuleOperator; value: number | string }): string => {
    const valueText = isRiskMetric(c.metric)
      ? riskLabel(String(c.value))
      : String(c.value);
    return `${metricLabel(c.metric)} ${c.operator} ${valueText}`;
  };

  /** Join conditions with the AND keyword for the card / preview summaries. */
  const conditionsSummary = (conditions: unknown): string => {
    // Newer rules store conditions as an array of { metric, operator, value }.
    if (Array.isArray(conditions)) {
      if (conditions.length === 0) return t('NoConditionsYet');
      return conditions.map(conditionText).join(` ${t('And')} `);
    }
    // Legacy rules stored conditions as an object (e.g.
    // { max_absolute_change, max_percentage_change }). Render its entries
    // readably instead of crashing the whole page on `.map`.
    if (conditions && typeof conditions === 'object') {
      const parts = Object.entries(conditions as Record<string, unknown>)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`);
      return parts.length ? parts.join(` ${t('And')} `) : t('NoConditionsYet');
    }
    return t('NoConditionsYet');
  };

  // ---------- popup open / close ----------

  const openAddPopup = () => {
    if (!canManageRules) return;
    setEditingRule(null);
    setFormState({
      ...initialFormState(),
      // Default scope to the active shop when one is selected so the common
      // case (a per-shop rule) is one fewer click.
      scope: activeShopSlug || ORG_DEFAULT_SCOPE,
    });
    setIsAddPopupOpen(true);
  };

  const openEditPopup = (rule: AutoApprovalRule) => {
    if (!canManageRules || !canManageRule(rule)) return;
    setEditingRule(rule);
    // A shop-scoped rule edits against the active shop slug — the list is
    // already scoped to it, so that's the shop it belongs to. Org defaults
    // keep the org sentinel.
    const ruleScope =
      rule.scope === 'shop'
        ? activeShopSlug || ORG_DEFAULT_SCOPE
        : ORG_DEFAULT_SCOPE;
    setFormState({
      name: rule.name,
      scope: ruleScope,
      platform: rule.platform || '',
      tool_name: rule.tool_name || '',
      conditions:
        Array.isArray(rule.conditions) && rule.conditions.length > 0
          ? rule.conditions.map((c) => ({
              metric: c.metric,
              operator: c.operator,
              value: String(c.value ?? ''),
            }))
          : [emptyCondition()],
      max_risk_level: (rule.max_risk_level as RuleRiskLevel) || 'low',
      cap_enabled:
        rule.max_daily_auto_approvals !== null &&
        rule.max_daily_auto_approvals !== undefined,
      max_daily_auto_approvals:
        rule.max_daily_auto_approvals != null
          ? String(rule.max_daily_auto_approvals)
          : '10',
      is_active: rule.is_active,
    });
    setIsAddPopupOpen(true);
  };

  const closeAddPopup = () => {
    setIsAddPopupOpen(false);
    setEditingRule(null);
    setFormState(initialFormState());
  };

  const openDeletePopup = (rule: AutoApprovalRule) => {
    if (!canManageRules || !canManageRule(rule)) return;
    setDeletingRule(rule);
    setIsDeletePopupOpen(true);
  };

  const closeDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setDeletingRule(null);
  };

  // ---------- conditions builder mutators ----------

  const updateCondition = (
    index: number,
    patch: Partial<ConditionDraft>
  ) => {
    setFormState((prev) => {
      const next = [...prev.conditions];
      const merged = { ...next[index], ...patch };
      // When switching to/from the risk_level metric, reset the value so we
      // never carry a number into the risk dropdown (or vice versa).
      if (patch.metric && patch.metric !== next[index].metric) {
        merged.value = patch.metric === 'risk_level' ? 'medium' : '';
      }
      next[index] = merged;
      return { ...prev, conditions: next };
    });
  };

  const addCondition = () => {
    setFormState((prev) => ({
      ...prev,
      conditions: [...prev.conditions, emptyCondition()],
    }));
  };

  const removeCondition = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  // ---------- validation + preview ----------

  /** Conditions whose value is filled in — the ones we'll actually send. */
  const filledConditions = useMemo(
    () => formState.conditions.filter((c) => String(c.value).trim() !== ''),
    [formState.conditions]
  );

  const isFormValid =
    formState.name.trim().length > 0 && filledConditions.length > 0;

  const previewConditions = useMemo(
    () =>
      filledConditions.map((c) => ({
        metric: c.metric,
        operator: c.operator,
        value: c.value,
      })),
    [filledConditions]
  );

  // ---------- build request body from form ----------

  const buildRuleBody = (): CreateAutoApprovalRuleInput => {
    const conditions: RuleCondition[] = filledConditions.map((c) => ({
      metric: c.metric,
      operator: c.operator,
      // Numeric metrics are sent as numbers; risk_level stays a string.
      value: isRiskMetric(c.metric)
        ? String(c.value)
        : Number(c.value),
    }));

    const body: CreateAutoApprovalRuleInput = {
      name: formState.name.trim(),
      conditions,
      platform: formState.platform || null,
      tool_name: formState.tool_name.trim() || null,
      max_risk_level: formState.max_risk_level,
      max_daily_auto_approvals: formState.cap_enabled
        ? parseInt(formState.max_daily_auto_approvals || '10', 10)
        : null,
      is_active: formState.is_active,
    };

    // Omit `shop` entirely for an org-wide default; otherwise target the slug.
    if (formState.scope !== ORG_DEFAULT_SCOPE) {
      body.shop = formState.scope;
    }
    return body;
  };

  // ---------- save / delete / toggle ----------

  const handleSaveRule = async () => {
    if (!canManageRules || !isFormValid) return;
    setIsSaving(true);
    try {
      const body = buildRuleBody();
      const result = editingRule
        ? await updateAutoApprovalRule(editingRule.id, body)
        : await createAutoApprovalRule(body);
      if (result) {
        dispatch(
          setMessage({
            message: editingRule
              ? t('RuleUpdatedSuccessfully')
              : t('RuleCreatedSuccessfully'),
            messageType: 'success',
          })
        );
        closeAddPopup();
        loadRules();
      } else {
        dispatch(
          setMessage({ message: t('FailedToSaveRule'), messageType: 'error' })
        );
      }
    } catch (error: any) {
      console.error('Failed to save auto-approval rule:', error);
      dispatch(
        setMessage({
          message: ruleErrorMessage(error, t('FailedToSaveRule')),
          messageType: 'error',
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!canManageRules || !deletingRule) return;
    setIsDeleting(true);
    try {
      const ok = await deleteAutoApprovalRule(deletingRule.id);
      if (ok) {
        dispatch(
          setMessage({
            message: t('RuleDeletedSuccessfully'),
            messageType: 'success',
          })
        );
        closeDeletePopup();
        loadRules();
      } else {
        dispatch(
          setMessage({
            message: t('FailedToDeleteRule'),
            messageType: 'error',
          })
        );
      }
    } catch (error: any) {
      console.error('Failed to delete auto-approval rule:', error);
      dispatch(
        setMessage({
          message: ruleErrorMessage(error, t('FailedToDeleteRule')),
          messageType: 'error',
        })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleRuleActive = async (
    rule: AutoApprovalRule,
    active: boolean
  ) => {
    if (!canManageRules || !canManageRule(rule)) return;
    // Optimistic flip; revert on failure.
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, is_active: active } : r))
    );
    try {
      await updateAutoApprovalRule(rule.id, { is_active: active });
    } catch (error: any) {
      console.error('Failed to toggle rule:', error);
      setRules((prev) =>
        prev.map((r) =>
          r.id === rule.id ? { ...r, is_active: !active } : r
        )
      );
      dispatch(
        setMessage({
          message:
            error?.code === ORG_WIDE_RULE_REQUIRES_ALL_BRANDS
              ? t('RuleOrgWideRequiresAllBrands')
              : t('FailedToSaveRule'),
          messageType: 'error',
        })
      );
    }
  };

  // ---------- render: rule card ----------

  const renderRuleCard = (rule: AutoApprovalRule) => {
    const isOrgDefault = rule.scope === 'org_default';
    const cap = rule.max_daily_auto_approvals;
    // The server decides per rule: an organisation-wide rule is not this
    // member's to manage unless they can access every brand.
    const ruleLocked = !canManageRule(rule);
    const canEditRule = canManageRules && !ruleLocked;
    return (
      <div key={rule.id} className='auto-rule-card'>
        <div
          className='auto-rule-card__body'
          onClick={canEditRule ? () => openEditPopup(rule) : undefined}
          style={{ cursor: canEditRule ? 'pointer' : 'default' }}
        >
          <div className='auto-rule-card__head'>
            <h3 className='auto-rule-card__name'>{rule.name}</h3>
            <span
              className={
                'scope-badge ' +
                (isOrgDefault ? 'scope-badge--org' : 'scope-badge--shop')
              }
            >
              {isOrgDefault
                ? t('ScopeOrgWide')
                : shopNameForSlug(activeShopSlug) || t('ScopeShop')}
            </span>
            {rule.platform && (
              <span className='platform-tag'>
                {platformLabel(rule.platform)}
              </span>
            )}
            {!rule.is_active && (
              <span className='status-badge status-badge--expired'>
                {t('Inactive')}
              </span>
            )}
          </div>

          <p className='auto-rule-card__summary'>
            <span className='auto-rule-card__summary-when'>{t('AutoApproveWhen')}:</span>{' '}
            {conditionsSummary(rule.conditions)}
          </p>

          <div className='auto-rule-card__meta'>
            <span
              className={
                'risk-pill risk-pill--' + (rule.max_risk_level || 'low')
              }
            >
              {t('UpToRisk', { risk: riskLabel(rule.max_risk_level) })}
            </span>
            {cap != null ? (
              <span className='auto-rule-card__cap'>
                {t('DailyCapUsage', {
                  used: rule.daily_auto_approvals_used ?? 0,
                  cap,
                })}
              </span>
            ) : (
              <span className='auto-rule-card__cap'>{t('NoDailyCap')}</span>
            )}
            {rule.tool_name && (
              <span className='auto-rule-card__tool'>{rule.tool_name}</span>
            )}
          </div>
        </div>

        {ruleLocked ? (
          <div className='auto-rule-card__actions'>
            <span className='auto-rule-card__locked'>
              {t('RuleManagedByAllBrandMembers')}
            </span>
          </div>
        ) : (
          <div className='auto-rule-card__actions'>
            <ToggleSwitch
              isOn={rule.is_active}
              onClick={(checked: boolean) =>
                handleToggleRuleActive(rule, checked)
              }
              id={`auto-rule-toggle-${rule.id}`}
              disabled={!canManageRules}
            />
            {canManageRules && (
              <div
                className='auto-rule-card__delete'
                onClick={() => openDeletePopup(rule)}
                title={t('DeleteRule')}
                data-testid={`auto-rule-delete-${rule.id}`}
              >
                <CustomSVGs svg={trash2SVG} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---------- render: recent matches ----------

  const renderRecentMatches = () => {
    if (recentMatches.length === 0) return null;
    return (
      <div className='auto-rule-recent'>
        <div className='auto-rule-recent__head'>
          <CustomSVGs svg={clockSVG} />
          <h3>{t('RecentAutoApprovals')}</h3>
        </div>
        <div className='auto-rule-recent__list'>
          {recentMatches.map((m) => {
            const rule = rules.find((r) => r.id === m.rule_id);
            const ruleName = rule?.name || t('AutoApprovalRule');
            const entity = m.entity_name || m.tool_name || t('AChange');
            const changeText =
              m.change_percentage != null
                ? ` (${m.change_percentage > 0 ? '+' : ''}${m.change_percentage}%)`
                : '';
            return (
              <div key={m.proposal_id} className='auto-rule-recent__item'>
                <span className='auto-rule-recent__dot' />
                <p className='auto-rule-recent__text'>
                  {t('RecentMatchLine', {
                    rule: ruleName,
                    entity: `${entity}${changeText}`,
                    platform: platformLabel(m.platform),
                  })}{' '}
                  <span className='auto-rule-recent__time'>
                    {timeAgo(m.auto_approved_at)}
                  </span>
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- render: conditions builder (popup) ----------

  const renderConditionsBuilder = () => (
    <div className='auto-rule-conditions'>
      <label className='auto-rule-conditions__label'>{t('Conditions')}</label>
      <p className='auto-rule-conditions__hint'>{t('ConditionsAllHint')}</p>
      {formState.conditions.map((c, i) => (
        <div className='auto-rule-condition-row' key={i}>
          {i > 0 && (
            <span className='auto-rule-condition-row__and'>{t('AndUpper')}</span>
          )}
          <select
            className='auto-rule-condition-row__metric'
            value={c.metric}
            onChange={(e) =>
              updateCondition(i, { metric: e.target.value as RuleMetric })
            }
          >
            {METRIC_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {metricLabel(m)}
              </option>
            ))}
          </select>
          <select
            className='auto-rule-condition-row__op'
            value={c.operator}
            onChange={(e) =>
              updateCondition(i, { operator: e.target.value as RuleOperator })
            }
          >
            {OPERATOR_OPTIONS.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          {isRiskMetric(c.metric) ? (
            <select
              className='auto-rule-condition-row__value'
              value={c.value || 'medium'}
              onChange={(e) => updateCondition(i, { value: e.target.value })}
            >
              {RISK_VALUE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {riskLabel(r)}
                </option>
              ))}
            </select>
          ) : (
            <input
              className='auto-rule-condition-row__value'
              type='number'
              step='any'
              value={c.value}
              placeholder={t('Value')}
              onChange={(e) => updateCondition(i, { value: e.target.value })}
            />
          )}
          <button
            type='button'
            className='auto-rule-condition-row__remove'
            onClick={() => removeCondition(i)}
            disabled={formState.conditions.length === 1}
            title={t('RemoveCondition')}
          >
            <CustomSVGs svg={trash2SVG} />
          </button>
        </div>
      ))}
      <button
        type='button'
        className='auto-rule-conditions__add'
        onClick={addCondition}
      >
        <CustomSVGs svg={plusSignSVG} />
        <span>{t('AddCondition')}</span>
      </button>
    </div>
  );

  // ---------- main render ----------

  return (
    <SubPageInnerContentHolder
      title={t('AutoApprovalRules')}
      backBtnLink={HOME_ROUTE}
      mainBtnTitle={canManageRules ? t('AddRule') : undefined}
      mainBtnIcon={canManageRules ? plusSignSVG : undefined}
      mainBtnLoading={false}
      mainBtnAction={canManageRules ? openAddPopup : undefined}
    >
      <ActionCenterTabs />
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingRules')} />
        </div>
      ) : accessDenied ? (
        <NoAccessNotice />
      ) : loadError ? (
        <div className='action-empty-state' role='alert'>
          <div className='action-error' style={{ textAlign: 'left' }}>
            <p className='action-error__text'>
              <strong>{t('FailedToLoadRules')}</strong>
            </p>
            <p className='action-error__text'>{loadError}</p>
          </div>
          <div style={{ marginTop: '1.2rem' }}>
            <MainSquareButton
              value={t('Retry')}
              type='button'
              buttonType='white'
              onClick={loadRules}
            />
          </div>
        </div>
      ) : (
        <>
          {rules.length === 0 ? (
            <div className='action-empty-state'>
              <p className='action-empty-state__text'>
                {t('NoAutoApprovalRulesTitle')}
              </p>
              <p className='auto-rule-empty__sub'>
                {t('NoAutoApprovalRulesSub')}
              </p>
            </div>
          ) : (
            <div className='action-rules-list'>
              {rules.map(renderRuleCard)}
            </div>
          )}

          {renderRecentMatches()}
        </>
      )}

      {/* Add / Edit Rule Popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeAddPopup}
        trigger={isAddPopupOpen}
      >
        <div className='auto-rule-form'>
          <h2 className='auto-rule-form__title'>
            {editingRule ? t('EditRule') : t('AddNewRule')}
          </h2>

          <div className='auto-rule-form__fields'>
            <div className='auto-rule-form__field'>
              <label>{t('RuleName')}</label>
              <input
                type='text'
                value={formState.name}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder={t('RuleNamePlaceholder')}
              />
            </div>

            <div className='auto-rule-form__field'>
              <label>{t('Scope')}</label>
              <select
                value={formState.scope}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, scope: e.target.value }))
                }
              >
                <option value={ORG_DEFAULT_SCOPE}>{t('ScopeOrgWide')}</option>
                {shopOptions.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.name}
                  </option>
                ))}
              </select>
              <p className='auto-rule-form__field-desc'>
                {formState.scope === ORG_DEFAULT_SCOPE
                  ? t('ScopeOrgWideDesc')
                  : t('ScopeShopDesc')}
              </p>
            </div>

            <div className='auto-rule-form__row'>
              <div className='auto-rule-form__field'>
                <label>{t('Platform')}</label>
                <select
                  value={formState.platform}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      platform: e.target.value,
                    }))
                  }
                >
                  <option value=''>{t('AllPlatforms')}</option>
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='auto-rule-form__field'>
                <label>{t('ToolFilterOptional')}</label>
                <input
                  type='text'
                  value={formState.tool_name}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      tool_name: e.target.value,
                    }))
                  }
                  placeholder={t('AllTools')}
                />
              </div>
            </div>

            {renderConditionsBuilder()}

            <div className='auto-rule-form__field'>
              <label>{t('MaxRiskLevel')}</label>
              <select
                value={formState.max_risk_level}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    max_risk_level: e.target.value as RuleRiskLevel,
                  }))
                }
              >
                {RISK_LEVEL_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {riskLabel(r)}
                  </option>
                ))}
              </select>
              <p className='auto-rule-form__field-desc'>
                {t('CriticalNeverAutoApproves')}
              </p>
            </div>

            <div className='auto-rule-form__toggle-row'>
              <div>
                <label>{t('LimitDailyAutoApprovals')}</label>
                <p className='auto-rule-form__field-desc'>
                  {t('LimitDailyAutoApprovalsDesc')}
                </p>
              </div>
              <ToggleSwitch
                isOn={formState.cap_enabled}
                onClick={(checked: boolean) =>
                  setFormState((prev) => ({ ...prev, cap_enabled: checked }))
                }
                id='auto-rule-cap-toggle'
              />
            </div>
            {formState.cap_enabled && (
              <div className='auto-rule-form__field'>
                <label>{t('MaxDailyAutoApprovals')}</label>
                <input
                  type='number'
                  min='1'
                  step='1'
                  value={formState.max_daily_auto_approvals}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      max_daily_auto_approvals: e.target.value,
                    }))
                  }
                  placeholder='10'
                />
              </div>
            )}

            <div className='auto-rule-form__toggle-row'>
              <label>{t('Active')}</label>
              <ToggleSwitch
                isOn={formState.is_active}
                onClick={(checked: boolean) =>
                  setFormState((prev) => ({ ...prev, is_active: checked }))
                }
                id='auto-rule-active-toggle'
              />
            </div>

            {/* Live human-readable preview */}
            <div className='auto-rule-preview'>
              <span className='auto-rule-preview__label'>{t('Preview')}</span>
              <p className='auto-rule-preview__text'>
                {previewConditions.length === 0
                  ? t('PreviewEmpty')
                  : t('PreviewLine', {
                      scope:
                        formState.scope === ORG_DEFAULT_SCOPE
                          ? t('ScopeOrgWide')
                          : shopNameForSlug(formState.scope),
                      conditions: conditionsSummary(previewConditions),
                      risk: riskLabel(formState.max_risk_level),
                    })}
              </p>
            </div>
          </div>

          <div className='auto-rule-form__buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isSaving}
              onClick={closeAddPopup}
            />
            <MainSquareButton
              value={editingRule ? t('UpdateRule') : t('CreateRule')}
              type='button'
              disabled={isSaving || !isFormValid}
              loading={isSaving}
              onClick={handleSaveRule}
            />
          </div>
        </div>
      </Popup>

      {/* Delete Confirmation Popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeDeletePopup}
        trigger={isDeletePopupOpen}
      >
        <div className='action-delete-confirm'>
          <div className='action-delete-confirm__title'>
            <div className='action-delete-confirm__title-icon'>
              <CustomSVGs svg={warningWithCircleSVG} />
            </div>
            <h3>{t('DeleteRule')}</h3>
          </div>
          <div className='action-delete-confirm__message'>
            <p>{t('AreYouSureYouWantToDeleteThisRule')}</p>
            {deletingRule && (
              <p>
                <strong>{t('Rule')}:</strong> {deletingRule.name}
              </p>
            )}
          </div>
          <div className='action-delete-confirm__buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isDeleting}
              onClick={closeDeletePopup}
            />
            <MainSquareButton
              value={t('Delete')}
              type='button'
              buttonType='delete'
              disabled={isDeleting}
              loading={isDeleting}
              onClick={handleDeleteRule}
            />
          </div>
        </div>
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default ActionRules;
